/**
 * 地磅连接混入（双轨：Web Serial + jinglunGo）
 *
 * [2026-08-10] 双轨并存：
 *   - 'web' 模式（默认）：浏览器 Web Serial API（老用户零感知，行为同改造前）
 *   - 'jinglun' 模式：jinglunGo 本地服务（127.0.0.1:8765，Web Serial 不可用时用）
 *
 * 用户在「系统管理→过磅设备」选择连接方式（getScaleConnectMode）。
 * 对外 method 签名不变（connectScale/disconnectScale/connectScaleWithProbe/
 * tryAutoConnectAuthorizedScales/disconnectAllScales），内部按开关调度到 _xxxWeb 或 _xxxJinglun。
 *
 * [2026-09-04] 数据门禁（全局不变式）: "串口 open 成功" ≠ "已连接"。错串口/错波特率时
 *   OS 层 open 恒成功，直接亮"已连接"会闪现 0kg 再断开误导用户。store 的 connected=true
 *   只能由"收到有效重量读数"触发（Web: onWeightChange 首帧; jinglun: WS reading /
 *   waitForReading 验证通过），所有 open/status 对账路径一律先过数据门禁。
 *
 * GlobalScaleBar 直接消费 Vuex store，与数据源解耦。
 */

import WeighbridgeReader, { probeBaudRate, friendlySerialError } from "@/utils/weighbridgeReader";
import ScaleClient, { ScaleStream, isServiceUnavailableError, smartConnectScale } from "@/utils/scaleClient";
// [2026-09-15] 地磅连接日志上报（前端埋点：连接成功/失败/断开+原因，运营周统计用）
import {
  initScaleConnectLog,
  noteConnectAttempt,
  noteConnectFail,
  noteDisconnectReason,
  noteManualClose,
  noteScaleError,
} from "@/utils/scaleConnectLog";
import {
  SCALE_PROBE_BAUD_RATES,
  poolVerifiedBaud,
  upsertScalePortPool,
  loadScaleBinding,
  saveScaleBinding,
  loadScaleAutoConnectedOnce,
  saveScaleAutoConnectedOnce,
  isScaleEnabled,
  getScaleConnectMode,
  scaleBaudMap,
  scaleProtocolMap,
  scaleComPortMap,
  loadHardwareDisabled,
} from "@/utils/weighDevice";

// scaleKey (entryScale/exitScale) → 磅配置 key (entry/exit)
const scaleKeyToWhich = (scaleKey) => (scaleKey === "entryScale" ? "entry" : "exit");

export default {
  data() {
    return {
      // WeighbridgeReader 实例映射（Web Serial 模式） { entryScale: reader, exitScale: reader }
      weighbridgeReaders: {},
      // 连接中的状态 { entryScale: boolean, exitScale: boolean }
      scaleConnecting: {},
      // 波特率探测中的状态 { entryScale: boolean, exitScale: boolean }
      scaleProbing: {},
    };
  },

  created() {
    // 非响应式内部状态
    this._scaleLastOpts = {};        // Web Serial: 每个磅最后一次连接参数（watchdog 自愈重连复用）
    this._scaleDataTimeoutRetried = {}; // Web Serial: 本次连接是否已因数据超时自愈过一次
    this._autoConnectTimer = null;
    this._autoConnectAttemptFailed = {}; // 自动连接首次失败的磅，组件生命周期内不再重试
    this._scaleUnsub = null;          // jinglun: WS 订阅取消函数
    // [2026-09-04] 手动连接连续失败计数（≥3 次弹 UPort 排障大提示）
    this._scaleConnectFailStreak = {};
    // [2026-08-24] 数据新鲜度看门狗（偶发"读数卡 0, 刷新才好"的兜底自愈）
    this._scaleFreshnessTimer = null;  // 看门狗轮询定时器
    this._scaleLastHealAt = {};        // 每磅最近一次自愈时刻（30s 内最多自愈一次）
    this._scaleConnectedAt = {};       // 每磅最近一次进入已连接状态的时刻（新连接 20s 内不自愈）
  },

  methods: {
    // ==================== 对外调度器（按连接方式开关分发）====================

    // [2026-08-14] "本机无外设硬件"总开关是否生效 (优先读 store 响应式值, 兜底 localStorage)
    _isHardwareDisabled() {
      try {
        const ds = this.$store && this.$store.state && this.$store.state.deviceStatus;
        if (ds && typeof ds.hardwareDisabled === "boolean") return ds.hardwareDisabled;
      } catch (e) { /* ignore */ }
      return loadHardwareDisabled();
    },

    async connectScale(scaleKey, options = {}) {
      if (this._isHardwareDisabled()) {
        // [2026-08-14] 本机无外设硬件: 不连地磅; 手动点击时提示, 自动/静默场景直接返回
        if (!options.silent) {
          this.$message.info("本机已停用外设硬件（设备管理→设备配置），如需连接请先关闭该开关");
        }
        return;
      }
      if (getScaleConnectMode() === "jinglun") {
        return this._connectScaleJinglun(scaleKey, options);
      }
      return this._connectScaleWeb(scaleKey, options);
    },

    async disconnectScale(scaleKey) {
      // [2026-09-16] 连接日志: 用户点"断开"是明确动作, 归因 manual_close（两种模式通用）
      noteManualClose(scaleKey);
      if (getScaleConnectMode() === "jinglun") {
        return this._disconnectScaleJinglun(scaleKey);
      }
      return this._disconnectScaleWeb(scaleKey);
    },

    async disconnectAllScales() {
      if (getScaleConnectMode() === "jinglun") {
        await Promise.all([this._disconnectScaleJinglun("entryScale"), this._disconnectScaleJinglun("exitScale")]);
        return;
      }
      const keys = Object.keys(this.weighbridgeReaders || {});
      await Promise.all(
        keys.map((key) => {
          if (this.weighbridgeReaders[key]) return this._disconnectScaleWeb(key);
          return Promise.resolve();
        }),
      );
      this.weighbridgeReaders = {};
      this.scaleConnecting = {};
    },

    async connectScaleWithProbe(scaleKey) {
      if (this._isHardwareDisabled()) {
        // [2026-08-14] 本机无外设硬件: 手动"连接"按钮拦截, 不探测不弹外设服务错误
        this.$message.info("本机已停用外设硬件（设备管理→设备配置），如需连接请先关闭该开关");
        return;
      }
      if (getScaleConnectMode() === "jinglun") {
        // [2026-09-04] 连续失败 ≥3 次 → 弹 UPort 排障大提示（黄灯/插拔/重装驱动）
        // undefined = 未真正尝试（如未配置串口），不计数
        const ok = await this._connectScaleWithProbeJinglun(scaleKey);
        if (ok !== undefined) this._noteScaleConnectOutcome(scaleKey, !!ok);
        return ok;
      }
      return this._connectScaleWithProbeWeb(scaleKey);
    },

    // [2026-09-04] 手动连接结果记账: 成功清零; 连续失败 ≥3 次弹一次 UPort 排障大提示
    // (弹完清零, 避免之后每次失败都弹; 用户可从提示再次进重装流程).
    _noteScaleConnectOutcome(scaleKey, ok) {
      if (!this._scaleConnectFailStreak) this._scaleConnectFailStreak = {};
      if (ok) {
        this._scaleConnectFailStreak[scaleKey] = 0;
        return;
      }
      this._scaleConnectFailStreak[scaleKey] = (this._scaleConnectFailStreak[scaleKey] || 0) + 1;
      if (this._scaleConnectFailStreak[scaleKey] >= 3) {
        this._scaleConnectFailStreak[scaleKey] = 0;
        this._showUPortHelpConfirm();
      }
    },

    // UPort 排障大提示: 黄灯闪烁检查 → 插拔 → 重装驱动（打开全局重装弹窗, 显示系统版本+官方策略）
    _showUPortHelpConfirm() {
      const h = this.$createElement;
      this.$confirm({
        title: "⚠ 您多次连接地磅失败",
        width: 520,
        content: h("div", { style: "line-height: 2.1; font-size: 14px;" }, [
          h("p", ["1. 查看电脑旁 ", h("b", "UPort 串口卡的黄灯是否在闪烁"), "（黄灯闪烁通常是驱动/连接异常）"]),
          h("p", ["2. ", h("b", "把 UPort 的 USB 线拔掉，等 5 秒再插回"), "，然后重新点「连接」"]),
          h("p", ["3. 以上均无效 → 点下方按钮 ", h("b", "重装 UPort 驱动"), "（自动识别您的系统是 Win7 / Win10 / Win11，按 MOXA 官方支持版本安装）"]),
        ]),
        okText: "重装 UPort 驱动",
        cancelText: "稍后再说",
        onOk: () => {
          this.$store.commit("SET_UPORT_REINSTALL_VISIBLE", true);
        },
      });
    },

    async tryAutoConnectAuthorizedScales() {
      if (getScaleConnectMode() === "jinglun") {
        return this._tryAutoConnectJinglun();
      }
      return this._tryAutoConnectWeb();
    },

    /**
     * 获取秤的中文标签。
     */
    _scaleLabel(scaleKey) {
      const labels = { entryScale: "小磅(入库)", exitScale: "大磅(出库)" };
      return labels[scaleKey] || scaleKey;
    },

    // ==================== Web Serial 模式（浏览器，从旧版恢复）====================

    async _connectScaleWeb(scaleKey, options = {}) {
      const silent = !!options.silent;

      // 检查浏览器支持
      if (!WeighbridgeReader.isSupported()) {
        if (!silent) {
          this.$message.warning(
            "您的浏览器不支持 Web Serial API，请在「系统管理→过磅设备」里切换为 jinglunGo 本地服务模式",
          );
        }
        return;
      }

      // 如果该秤已有读取器在连接中，先断开
      if (this.weighbridgeReaders[scaleKey]) {
        await this._disconnectScaleWeb(scaleKey);
      }

      // 记录本次连接参数（watchdog 自愈重连时复用）
      this._scaleLastOpts[scaleKey] = { ...options };

      if (!silent) this.$set(this.scaleConnecting, scaleKey, true);

      const reader = new WeighbridgeReader();

      // [2026-09-04] 数据门禁: 串口打开 ≠ 已连接。错串口/错波特率时 OS 层 open 恒成功,
      // 若 open 即亮"已连接"会闪现"已连接 + 0kg"再断开。改为首个有效重量到达才算连接成功。
      let announced = false;
      reader.setOnWeightChange((weight, stable) => {
        this._scaleDataTimeoutRetried[scaleKey] = false;
        this.$store.commit("SET_SCALE", { scale: scaleKey, weight, stable });
        // [2026-09-16] 防重复提交: 读数每 ~100ms 一帧, connected 已是 true 就不再 commit。
        // 原来每帧都 commit, 连接日志上报端(状态闸门上线前)一天能刷出近十万条 connect_ok。
        const stNow = this.$store.state.deviceStatus[scaleKey];
        if (!stNow || !stNow.connected) {
          this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: true });
        }
        if (!announced) {
          announced = true;
          this._scaleConnectedAt[scaleKey] = Date.now();
          saveScaleAutoConnectedOnce(scaleKey);
          if (!silent) this.$message.success(`${this._scaleLabel(scaleKey)}已连接`);
        }
      });
      reader.setOnConnect(() => {
        // 串口已打开 — 只解除"连接中"，不亮"已连接"（等 onWeightChange 首帧）
        this.$set(this.scaleConnecting, scaleKey, false);
      });
      reader.setOnDisconnect(() => {
        this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: false });
        this.$store.commit("SET_SCALE", { scale: scaleKey, weight: 0, stable: false });
        this.$set(this.scaleConnecting, scaleKey, false);
      });
      reader.setOnError((msg) => {
        console.warn(`[地磅 ${scaleKey}] ${msg}`);
        // [2026-09-16] 连接日志归因: web 模式的串口错误此前到不了上报模块,
        // 断开后只能记"未知"。暂存 60s, 断开事件就近取用。
        noteScaleError(scaleKey, "READ_ERROR", typeof msg === "string" ? msg : String(msg));
        if (!reader.isConnected()) {
          if (!silent) {
            this.$message.warning(`${this._scaleLabel(scaleKey)}连接失败: ${typeof msg === "string" ? msg : friendlySerialError(msg)}`);
          }
          this.$set(this.scaleConnecting, scaleKey, false);
        }
      });
      reader.setOnDataTimeout((info) => {
        this._handleScaleDataTimeoutWeb(scaleKey, info);
      });

      this.$set(this.weighbridgeReaders, scaleKey, reader);

      const readerOptions = { ...options };
      delete readerOptions.silent;
      const success = await reader.connect(readerOptions);
      if (!success) {
        this.$set(this.scaleConnecting, scaleKey, false);
        if (this.weighbridgeReaders[scaleKey] === reader) {
          this.$set(this.weighbridgeReaders, scaleKey, null);
        }
        if (silent || options._autoConnectAttempt) {
          this._autoConnectAttemptFailed[scaleKey] = true;
        }
      }
    },

    async _disconnectScaleWeb(scaleKey) {
      const reader = this.weighbridgeReaders[scaleKey];
      if (reader) {
        try { await reader.destroy(); } catch (e) { /* 忽略 */ }
        this.$set(this.weighbridgeReaders, scaleKey, null);
        this.$set(this.scaleConnecting, scaleKey, false);
        this._scaleDataTimeoutRetried[scaleKey] = false;
        this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: false });
        this.$store.commit("SET_SCALE", { scale: scaleKey, weight: 0, stable: false });
      }
    },

    async _connectScaleWithProbeWeb(scaleKey) {
      if (!WeighbridgeReader.isSupported()) {
        this.$message.warning("您的浏览器不支持 Web Serial API，请在「系统管理→过磅设备」切换为 jinglunGo 模式");
        return;
      }
      if (this.weighbridgeReaders[scaleKey]) return;

      const portMap = this.weighbridgePortMap || {};
      const desc = portMap[scaleKey];
      const configuredBaud = (this.weighbridgeBaudRateMap || {})[scaleKey];
      const protocol = (this.weighbridgeProtocolMap || {})[scaleKey] || undefined;

      let ports = [];
      try { ports = await navigator.serial.getPorts(); } catch (e) { ports = []; }

      // ① 端口池已验证该端口的波特率 → 直连，跳过遍历
      const poolBaud = desc ? poolVerifiedBaud(desc) : null;
      if (poolBaud) {
        const sp = this._resolveSerialPort(ports, desc);
        if (sp) {
          await this._connectScaleWeb(scaleKey, { presetPort: sp, baudRate: poolBaud, ...(protocol ? { protocol } : {}) });
          return;
        }
      }

      let serialPort = this._resolveSerialPort(ports, desc);
      if (!serialPort) {
        try {
          serialPort = await navigator.serial.requestPort();
        } catch (e) { return; }
      }
      if (!serialPort) return;

      const candidates = [];
      if (configuredBaud) candidates.push(Number(configuredBaud));
      for (const b of SCALE_PROBE_BAUD_RATES) {
        if (!candidates.includes(Number(b))) candidates.push(Number(b));
      }

      this.$set(this.scaleProbing, scaleKey, true);
      this.$set(this.scaleConnecting, scaleKey, true);
      let hitBaud = null;
      try {
        const label = this._scaleLabel(scaleKey);
        hitBaud = await probeBaudRate(serialPort, protocol, candidates, {
          timeout: 3000,
          onProgress: (baud, ok) => {
            if (ok) this.$message.info(`${label} 探测成功: ${baud}`);
          },
        });
      } finally {
        this.$set(this.scaleProbing, scaleKey, false);
        this.$set(this.scaleConnecting, scaleKey, false);
      }

      if (!hitBaud) {
        this.$message.error(
          `${this._scaleLabel(scaleKey)}自动探测失败: 4 档波特率都读不出有效重量。请检查地磅电源/接线，或在「系统管理→过磅设备」手动配置。`,
        );
        return;
      }

      await this._recordProbedPort(serialPort, hitBaud, protocol);
      await this._connectScaleWeb(scaleKey, { presetPort: serialPort, baudRate: hitBaud, ...(protocol ? { protocol } : {}) });
    },

    async _tryAutoConnectWeb() {
      if (!WeighbridgeReader.isSupported()) return;
      let ports;
      try { ports = await navigator.serial.getPorts(); } catch (e) { return; }
      if (!ports || !ports.length) return;

      const baudMap = this.weighbridgeBaudRateMap || {};
      const portMap = this.weighbridgePortMap || {};
      const protocolMap = this.weighbridgeProtocolMap || {};
      const usedIdx = new Set();
      const baudFor = (desc, scaleKey) => (desc && poolVerifiedBaud(desc)) || (baudMap[scaleKey] || undefined);

      // 1) 优先按 USB 描述符匹配
      for (const scaleKey of Object.keys(portMap)) {
        const desc = portMap[scaleKey];
        if (!desc || desc.usbVendorId == null || this.weighbridgeReaders[scaleKey]) continue;
        if (!isScaleEnabled(scaleKeyToWhich(scaleKey))) continue;
        if (this._autoConnectAttemptFailed[scaleKey]) continue;
        const matches = [];
        ports.forEach((p, i) => {
          const x = (p.getInfo && p.getInfo()) || {};
          if (x.usbVendorId === desc.usbVendorId && x.usbProductId === desc.usbProductId) matches.push(i);
        });
        let pick = matches[desc.occurrence || 0];
        if (pick == null || usedIdx.has(pick)) pick = matches.find((i) => !usedIdx.has(i));
        if (pick != null) {
          usedIdx.add(pick);
          const opts = { presetPort: ports[pick], _autoConnectAttempt: true };
          const b = baudFor(desc, scaleKey);
          if (b) opts.baudRate = b;
          if (protocolMap[scaleKey]) opts.protocol = protocolMap[scaleKey];
          if (loadScaleAutoConnectedOnce(scaleKey)) opts.silent = true;
          await this._connectScaleWeb(scaleKey, opts);
        }
      }

      // 2) 回退「第几个已授权串口」映射
      const idxMap = this.weighbridgeAutoConnectMap || (ports.length === 1 ? { entryScale: 0 } : { entryScale: 0, exitScale: 1 });
      for (const scaleKey of Object.keys(idxMap)) {
        const d = portMap[scaleKey];
        if (d && d.usbVendorId != null) continue;
        if (this.weighbridgeReaders[scaleKey]) continue;
        if (!isScaleEnabled(scaleKeyToWhich(scaleKey))) continue;
        if (this._autoConnectAttemptFailed[scaleKey]) continue;
        const idx = idxMap[scaleKey];
        if (ports[idx] && !usedIdx.has(idx)) {
          usedIdx.add(idx);
          const opts = { presetPort: ports[idx], _autoConnectAttempt: true };
          if (baudMap[scaleKey]) opts.baudRate = baudMap[scaleKey];
          if (protocolMap[scaleKey]) opts.protocol = protocolMap[scaleKey];
          if (loadScaleAutoConnectedOnce(scaleKey)) opts.silent = true;
          await this._connectScaleWeb(scaleKey, opts);
        }
      }
    },

    _resolveSerialPort(ports, desc) {
      if (!desc || desc.usbVendorId == null || !Array.isArray(ports)) return null;
      const matches = [];
      ports.forEach((p) => {
        const x = (p.getInfo && p.getInfo()) || {};
        if (x.usbVendorId === desc.usbVendorId && x.usbProductId === desc.usbProductId) matches.push(p);
      });
      const pick = matches[desc.occurrence || 0];
      return pick != null ? pick : (matches[0] || null);
    },

    async _recordProbedPort(serialPort, baudRate, protocol) {
      const info = (serialPort && serialPort.getInfo && serialPort.getInfo()) || {};
      if (info.usbVendorId == null) return;
      let occurrence = 0;
      try {
        const all = await navigator.serial.getPorts();
        const same = all.filter((p) => {
          const x = (p.getInfo && p.getInfo()) || {};
          return x.usbVendorId === info.usbVendorId && x.usbProductId === info.usbProductId;
        });
        const idx = same.indexOf(serialPort);
        occurrence = idx >= 0 ? idx : 0;
      } catch (e) { /* 忽略 */ }
      try {
        upsertScalePortPool({
          port: { usbVendorId: Number(info.usbVendorId), usbProductId: Number(info.usbProductId), occurrence },
          baudRate: Number(baudRate),
          protocol: protocol || "yaohua-continuous",
        });
      } catch (e) { /* 忽略 */ }
    },

    async _handleScaleDataTimeoutWeb(scaleKey, info) {
      // [2026-09-16] 连接日志归因: 数据超时是可分辨的原因 — 有原始字节但解析不出帧
      // = 波特率/协议不对; 完全无数据 = 拔线/断电/线松。暂存给随后的断开事件就近取用。
      noteScaleError(
        scaleKey,
        info && info.rawDataReceived ? "DATA_TIMEOUT_PARSED" : "DATA_TIMEOUT_SILENT",
        info && info.rawDataReceived
          ? "数据超时：收到原始数据但解析不出有效重量（多为波特率/协议不匹配）"
          : "数据超时：完全收不到串口数据（拔线/断电/线松）",
      );
      if (this._scaleDataTimeoutRetried[scaleKey]) {
        this._autoConnectAttemptFailed[scaleKey] = true;
        return;
      }
      const opts = { ...(this._scaleLastOpts[scaleKey] || {}) };
      if (!opts.presetPort) {
        return; // 手动连接（无 presetPort）无法静默重连
      }
      this._scaleDataTimeoutRetried[scaleKey] = true;
      if (info && info.rawDataReceived) {
        // 有数据但解析失败 → 波特率不匹配，自动探测
        await this._autoDetectBaudRateWeb(scaleKey, opts);
      } else {
        // 完全无数据 → 端口降级，同参数重连
        await this._disconnectScaleWeb(scaleKey);
        await new Promise((r) => setTimeout(r, 500));
        await this._connectScaleWeb(scaleKey, opts);
      }
    },

    async _autoDetectBaudRateWeb(scaleKey, baseOpts) {
      const cfgBaud = Number(baseOpts.baudRate) || 9600;
      const candidates = [...new Set([cfgBaud, 9600, 1200])];
      this.$message.loading({ content: `${this._scaleLabel(scaleKey)}正在自动检测波特率...`, key: `baud-detect-${scaleKey}`, duration: 0 });
      await this._disconnectScaleWeb(scaleKey);
      this.$set(this.scaleConnecting, scaleKey, true);
      let detected = null;
      for (const baud of candidates) {
        const ok = await this._tryBaudRateWeb(scaleKey, baseOpts, baud);
        if (ok) { detected = baud; break; }
      }
      if (detected == null) {
        this.$message.destroy(`baud-detect-${scaleKey}`);
        this.$set(this.scaleConnecting, scaleKey, false);
        this.$message.error(`${this._scaleLabel(scaleKey)}地磅信息解析错误，请尝试手动连接`);
        return;
      }
      if (detected !== cfgBaud) {
        const which = scaleKeyToWhich(scaleKey);
        const scales = loadScaleBinding();
        const s = scales.find((x) => x.key === which);
        if (s) { s.baudRate = detected; saveScaleBinding(scales); }
        this.$message.success({ content: `${this._scaleLabel(scaleKey)}波特率自动检测为 ${detected}，已保存配置`, key: `baud-detect-${scaleKey}`, duration: 4 });
      } else {
        this.$message.destroy(`baud-detect-${scaleKey}`);
      }
      await new Promise((r) => setTimeout(r, 300));
      await this._connectScaleWeb(scaleKey, { ...baseOpts, baudRate: detected });
    },

    async _tryBaudRateWeb(scaleKey, baseOpts, baud) {
      const reader = new WeighbridgeReader();
      let gotFrame = false;
      reader.setOnWeightChange(() => { gotFrame = true; });
      const ok = await reader.connect({ ...baseOpts, baudRate: baud });
      if (!ok) return false;
      await new Promise((r) => setTimeout(r, 1800));
      await reader.destroy();
      return gotFrame;
    },

    // ==================== jinglunGo 模式（本地服务，Phase 2 实现）====================

    async _connectScaleJinglun(scaleKey, options = {}) {
      const silent = !!options.silent;
      const which = scaleKeyToWhich(scaleKey);
      let comPort = options.port;
      let baudRate = options.baudRate;
      let protocol = options.protocol;
      if (!comPort) comPort = scaleComPortMap()[scaleKey];
      if (!baudRate) baudRate = scaleBaudMap()[scaleKey] || 9600;
      if (!protocol) protocol = scaleProtocolMap()[scaleKey] || "yaohua-continuous";
      if (!comPort) {
        if (!silent) {
          this.$message.warning(`${this._scaleLabel(scaleKey)}未配置串口，请在「系统管理→过磅设备」里配置 COM 口`);
        }
        return;
      }
      if (!silent) this.$set(this.scaleConnecting, scaleKey, true);
      try {
        // [2026-09-04] 数据门禁: startedAt 取在 open 之前，open 请求期间到达的读数也算数
        noteConnectAttempt(scaleKey, { port: comPort, baudRate: parseInt(baudRate, 10) || 9600, protocol });
        const startedAt = Date.now();
        await ScaleClient.openScale({ scaleKey: which, port: comPort, baudRate: parseInt(baudRate, 10) || 9600, protocol });
        // 串口 open 成功 ≠ 连接成功: 等首个有效读数; 等不到按失败处理并关口
        // (closeScale 会写服务端 desired=false，同步止住调和循环的空转重连)
        const proved = await ScaleClient.waitForReading(which, 5000, startedAt);
        if (!proved) {
          try { await ScaleClient.closeScale(which); } catch (e) { /* 忽略 */ }
          this._autoConnectAttemptFailed[scaleKey] = true;
          noteConnectFail(scaleKey, { code: "NO_DATA", message: `串口 ${comPort} 能打开但收不到地磅数据` },
            { port: comPort, baudRate: parseInt(baudRate, 10) || 9600, protocol });
          if (!silent) {
            this.$message.warning(`${this._scaleLabel(scaleKey)}连接失败: 串口 ${comPort} 能打开但收不到地磅数据，请检查串口/波特率/协议配置`);
          }
          return;
        }
        this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: true });
        this._scaleConnectedAt[scaleKey] = Date.now();
        saveScaleAutoConnectedOnce(scaleKey);
        this._reconcileScaleStatus(); // WS 断链时 waitForReading 靠服务端快照验证, 顺带回填真实重量
        if (!silent) this.$message.success(`${this._scaleLabel(scaleKey)}已连接 (${comPort})`);
      } catch (e) {
        noteConnectFail(scaleKey, e, { port: comPort, baudRate: parseInt(baudRate, 10) || 9600, protocol });
        if (!silent) {
          // [2026-08-17] 连接失败若疑似串口被占 → 自动查占用者, 提示用户该关谁
          let msg = `${this._scaleLabel(scaleKey)}连接失败: ${e.message || e.code}`;
          if (e.code === "SCALE_OPEN_FAILED" && String(e.message || "").indexOf("占用") >= 0) {
            const hint = await this._checkPortHolderHint(comPort);
            if (hint) msg += `。${hint}`;
          }
          this.$message.warning(msg);
        }
        if (silent || options._autoConnectAttempt) this._autoConnectAttemptFailed[scaleKey] = true;
      } finally {
        if (!silent) this.$set(this.scaleConnecting, scaleKey, false);
      }
    },

    // [2026-08-17] 查询串口占用者并生成一句可操作的提示（查不到返回空串, 静默失败）
    async _checkPortHolderHint(comPort) {
      try {
        const r = await ScaleClient.checkPortHolder(comPort);
        const holders = (r && r.holders) || [];
        if (r && r.selfHolding) return `检测到 ${comPort} 正由本服务（好粮宝外设服务）持有`;
        if (holders.length) {
          const h = holders[0];
          const desc = h.hint ? h.hint : `${h.name}（PID ${h.pid}）`;
          return `检测到 ${comPort} 正被「${h.name}」占用 — ${desc}`;
        }
        // [2026-10-05] 浏览器自身的 Web Serial 也会持口（设备测试页「Web 原生页签」终端、
        // 旧标签页的 web 模式自动连接），占用者是浏览器进程——任务管理器里不像串口程序，
        // 现场"明明没程序占用"多为此因。给出口径而不是空手而归。
        const browserTip = "浏览器也会持口（设备测试页「Web 原生页签」终端或其它标签页的 Web Serial 连接），请刷新页面（F5）或关闭其它系统页面后重试";
        if (r && r.suspects && r.suspects.length) {
          const names = [...new Set(r.suspects.map(s => s.name))].slice(0, 3).join("、");
          return `${comPort} 被占用但未能定位占用者；正在运行的常见嫌疑程序：${names}。${browserTip}`;
        }
        return `未能定位占用者——${browserTip}`;
      } catch (e) { /* 服务不在线等情况静默 */ }
      return "";
    },

    async _disconnectScaleJinglun(scaleKey) {
      const which = scaleKeyToWhich(scaleKey);
      // 手动断开的归因标记由 disconnectScale 调度器统一设置
      try { await ScaleClient.closeScale(which); } catch (e) { /* 忽略 */ }
      this.$set(this.scaleConnecting, scaleKey, false);
      this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: false });
      this.$store.commit("SET_SCALE", { scale: scaleKey, weight: 0, stable: false });
    },

    async _connectScaleWithProbeJinglun(scaleKey) {
      const which = scaleKeyToWhich(scaleKey);
      const comMap = scaleComPortMap();
      const comPort = comMap[scaleKey];
      const protocol = scaleProtocolMap()[scaleKey] || "yaohua-continuous";
      if (!comPort) {
        this.$message.warning(`${this._scaleLabel(scaleKey)}未配置串口，请在「系统管理→过磅设备」里配置 COM 口`);
        return;
      }
      this.$set(this.scaleProbing, scaleKey, true);
      this.$set(this.scaleConnecting, scaleKey, true);
      const probeCtx = { port: comPort, baudRate: scaleBaudMap()[scaleKey] || 9600, protocol };
      noteConnectAttempt(scaleKey, probeCtx);
      try {
        // [2026-09-04] 委托给通用智能连接模块(scaleClient.smartConnectScale)：
        //   ① 按已保存参数（之前成功过的组合）直连+数据门禁
        //   ② 失败 → 统一端口诊断逐步排查（存在性→占用→驱动→协议+波特率）
        //   ③ 命中 → 用识别出的参数重连；仍失败/诊断失败 → 返回根因
        const label = this._scaleLabel(scaleKey);
        let diagnoseToastShown = false;
        const r = await smartConnectScale({
          scaleKey: which,
          port: comPort,
          baudRate: scaleBaudMap()[scaleKey] || 9600,
          protocol,
          onStage: (stage) => {
            if (stage === "diagnose" && !diagnoseToastShown) {
              diagnoseToastShown = true;
              this.$message.info(`${label}按原参数连不上，正在按检测步骤自动排查（存在性→占用→协议→波特率）…`);
            }
          },
        });
        if (r.ok) {
          // 诊断识别出新参数 → 回写配置，下次直连直接用
          if (r.via === "diagnose" && (r.baudRate !== Number(scaleBaudMap()[scaleKey]) || r.protocol !== protocol)) {
            const scales = loadScaleBinding();
            const s = scales.find((x) => x.key === which);
            if (s) {
              s.baudRate = r.baudRate;
              s.protocol = r.protocol;
              saveScaleBinding(scales);
            }
          }
          this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: true });
          this._scaleConnectedAt[scaleKey] = Date.now();
          saveScaleAutoConnectedOnce(scaleKey);
          this._reconcileScaleStatus(); // WS 断链时 waitForReading 靠服务端快照验证, 顺带回填真实重量
          this.$message.success(
            `${label}已连接 (${r.port} @${r.baudRate})${r.via === "diagnose" ? "（参数由端口检测自动识别并已保存）" : ""}`,
          );
          return true;
        }
        // 失败：诊断已定位根因，原样告诉用户（含解决方法首行）
        const firstSolution = (r.failSolution || "").split("\n")[0] || "";
        noteConnectFail(scaleKey, { code: r.failStep || "SMART_CONNECT_FAILED", message: r.failSummary }, probeCtx);
        this.$message.error({
          content: `${label}连接失败：${r.failSummary}${firstSolution ? `\n${firstSolution}` : ""}`,
          duration: 10,
        });
        return false;
      } catch (e) {
        noteConnectFail(scaleKey, e, probeCtx);
        if (isServiceUnavailableError(e)) {
          // [2026-10-11] 文案收口到统一漏斗: scaleClient 归一化时 message 已带
          // '外设服务不可达。'+peripheralAccessHint() (含弹窗触发), 不再硬编码第二套口径
          this.$message.error({ content: (e && e.message) || '外设服务不可达', duration: 8 });
        } else {
          this.$message.warning(`${this._scaleLabel(scaleKey)}连接失败: ${e.message || e.code}`);
        }
        return false;
      } finally {
        this.$set(this.scaleProbing, scaleKey, false);
        this.$set(this.scaleConnecting, scaleKey, false);
      }
    },

    async _tryAutoConnectJinglun() {
      try { await ScaleClient.health(); } catch (e) {
        // [2026-08-17] 服务刚启动/慢启动: 30s 后重试, 最多 10 次（约 5 分钟）
        this._scheduleAutoConnectRetry();
        return;
      }
      const comMap = scaleComPortMap();
      const baudMap = scaleBaudMap();
      const protocolMap = scaleProtocolMap();
      for (const scaleKey of Object.keys(comMap)) {
        if (this._autoConnectAttemptFailed[scaleKey]) continue;
        if (!isScaleEnabled(scaleKeyToWhich(scaleKey))) continue;
        // [2026-08-17] 幂等: 服务端已连着（其它标签页/测试页/服务自启连的）不重复 open
        const st = this.$store.state.deviceStatus[scaleKey];
        if (st && st.connected) continue;
        const comPort = comMap[scaleKey];
        if (!comPort) continue;
        const opts = { port: comPort, _autoConnectAttempt: true };
        if (baudMap[scaleKey]) opts.baudRate = baudMap[scaleKey];
        if (protocolMap[scaleKey]) opts.protocol = protocolMap[scaleKey];
        if (loadScaleAutoConnectedOnce(scaleKey)) opts.silent = true;
        await this._connectScaleJinglun(scaleKey, opts);
      }
    },

    // [2026-08-17] 自动连接重试: 外设服务未就绪时 30s×10 次兜底（服务端调和循环为主, 这是网页侧保险）
    _scheduleAutoConnectRetry() {
      if (this._autoConnectRetryTimer) return;
      this._autoConnectRetryCount = (this._autoConnectRetryCount || 0) + 1;
      if (this._autoConnectRetryCount > 10) return;
      this._autoConnectRetryTimer = setTimeout(() => {
        this._autoConnectRetryTimer = null;
        if (getScaleConnectMode() !== "jinglun") return;
        this.tryAutoConnectAuthorizedScales();
      }, 30000);
    },

    _subscribeScaleStream() {
      if (this._scaleUnsub) return;
      this._scaleUnsub = ScaleStream.subscribe(
        (msg) => {
          if (!msg || !msg.scaleKey) return;
          const scaleKey = msg.scaleKey === "entry" ? "entryScale" : "exitScale";
          // [2026-10-08 性能] 读数 commit 节流 (仪表约 10 帧/秒/磅, 双磅 = 每秒约 20 次 Vuex
          //   reactive commit 驱动全链路重算, 是过磅页常驻 CPU 底噪). 150ms 时间窗 + stable 沿
          //   立即放行 + trailing 保末值 (停稳后的最终读数必落地, 供"自动跟磅取数"消费).
          this._throttleScaleCommit(scaleKey, msg.weight, !!msg.stable);
          // [2026-09-04] 数据门禁: 收到有效读数 = 连接被数据证实，这一刻才亮"已连接"
          const st = this.$store.state.deviceStatus[scaleKey];
          if (!st || !st.connected) {
            this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: true });
            this._scaleConnectedAt[scaleKey] = Date.now();
          }
        },
        (msg) => {
          if (!msg || !msg.scaleKey) return;
          const scaleKey = msg.scaleKey === "entry" ? "entryScale" : "exitScale";
          if (msg.connected !== undefined) {
            if (msg.connected) {
              // [2026-09-04] 数据门禁: 服务端"串口已打开"不亮"已连接" — 只代表 open 成功,
              // 错串口/错波特率时也恒为 true, 直接显示会闪现"已连接 + 0kg"再断开。
              // 等 reading 回调（有效读数）来点亮。
              return;
            }
            this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected: false });
            this.$store.commit("SET_SCALE", { scale: scaleKey, weight: 0, stable: false });
          }
        },
        (msg) => { if (msg && msg.scaleKey) console.warn(`[地磅 ${msg.scaleKey}] ${msg.code}: ${msg.message}`); },
        // [2026-08-17] WS (重)连成功 → 与服务端状态对账（修复断线期间状态不同步的"卡未连接"）
        () => { this._reconcileScaleStatus(); }
      );
    },

    // [2026-10-08 性能] SET_SCALE 读数 commit 节流: 150ms 时间窗; stable 沿 (false→true/true→false)
    // 立即放行不丢信号; 窗口内暂存 pending 由 trailing 定时器落地末值. 同值重复帧被窗口自然吸收.
    _throttleScaleCommit(scaleKey, weight, stable) {
      const now = Date.now();
      const all = (this._scaleThrottle = this._scaleThrottle || {});
      let rec = all[scaleKey];
      if (!rec) {
        rec = all[scaleKey] = { at: 0, lastStable: null, pending: null, timer: null };
      }
      const stableEdge = stable !== rec.lastStable;
      if (stableEdge || now - rec.at >= 150) {
        if (rec.timer) { clearTimeout(rec.timer); rec.timer = null; }
        rec.pending = null;
        rec.at = now;
        rec.lastStable = stable;
        this.$store.commit("SET_SCALE", { scale: scaleKey, weight, stable });
        return;
      }
      rec.pending = { weight, stable };
      if (!rec.timer) {
        rec.timer = setTimeout(() => {
          rec.timer = null;
          const p = rec.pending;
          if (!p) return;
          rec.pending = null;
          rec.at = Date.now();
          rec.lastStable = p.stable;
          this.$store.commit("SET_SCALE", { scale: scaleKey, weight: p.weight, stable: p.stable });
        }, 150 - (now - rec.at));
      }
    },

    // [2026-08-17] 状态对账: 以服务端 /api/scale/status 为准刷新 store 双磅状态。
    // WS 重连/页面刷新后调用; 服务端串口由调和循环自愈, 这里只管展示层跟上事实。
    // [2026-09-04] 数据门禁: 服务端 connected=true 只代表串口 open 成功(错口/错波特率也恒 true),
    //   必须 15s 内解析出过有效帧(lastFrameAt)才对账成"已连接", 否则刷新页面会闪现 0kg。
    async _reconcileScaleStatus() {
      if (getScaleConnectMode() !== "jinglun") return;
      try {
        const st = await ScaleClient.getStatus();
        ["entry", "exit"].forEach((k) => {
          const one = st && st[k];
          if (!one) return;
          const scaleKey = k === "entry" ? "entryScale" : "exitScale";
          const proved = !!(one.lastFrameAt && Date.now() - one.lastFrameAt < 15000);
          const connected = !!one.connected && proved;
          this.$store.commit("SET_SCALE_CONNECTED", { scale: scaleKey, connected });
          if (connected) {
            this.$store.commit("SET_SCALE", { scale: scaleKey, weight: one.weight || 0, stable: !!one.stable });
          } else {
            this.$store.commit("SET_SCALE", { scale: scaleKey, weight: 0, stable: false });
          }
        });
      } catch (e) { /* 服务离线: WS 重连时会再对账 */ }
    },

    // ==================== [2026-08-24] 数据新鲜度看门狗 ====================
    // 背景: 偶发"地磅读数一直卡 0, 刷新页面就恢复"。排查确认服务端串口链路有
    // watchdog+调和循环兜底, 但两类僵死只能靠刷新才能被发现:
    //   ① WS 推送通道僵死（半开连接/服务端写阻塞）: 服务端数据新鲜但页面收不到;
    //   ② 串口句柄静默停止出数且服务端自愈未覆盖的边缘时序。
    // 策略: 每 10s 巡检; connected=true 但 >15s 无 reading 推送（仪表约 10 帧/秒）→
    //   先 HTTP 对账 /api/scale/status: 服务端 5s 内有原始字节 → 通道问题, 强制重建 WS;
    //   否则 → 串口侧僵死, open force=true 强制关重开。每磅 30s 内最多自愈一次。
    _startScaleFreshnessWatchdog() {
      if (this._scaleFreshnessTimer) return;
      this._scaleFreshnessTimer = setInterval(() => {
        try { this._checkScaleFreshness(); } catch (e) { /* 看门狗自身不能抛 */ }
      }, 10000);
    },

    _stopScaleFreshnessWatchdog() {
      if (this._scaleFreshnessTimer) { clearInterval(this._scaleFreshnessTimer); this._scaleFreshnessTimer = null; }
    },

    async _checkScaleFreshness() {
      if (getScaleConnectMode() !== "jinglun") return;
      const now = Date.now();
      for (const scaleKey of ["entryScale", "exitScale"]) {
        const st = this.$store.state.deviceStatus[scaleKey];
        if (!st || !st.connected) continue;
        // 刚建立的连接给 20s 出数窗口, 避免误杀首次连接/自愈重开的启动期
        if (this._scaleConnectedAt[scaleKey] && now - this._scaleConnectedAt[scaleKey] < 20000) continue;
        // 30s 内已自愈过一次仍卡 → 跳过本轮（防循环轰炸; 下一轮继续）
        if (this._scaleLastHealAt[scaleKey] && now - this._scaleLastHealAt[scaleKey] < 30000) continue;
        // 自动连接已判失败的磅不救（配置/硬件问题, 救了也是循环）
        if (this._autoConnectAttemptFailed[scaleKey]) continue;
        const which = scaleKeyToWhich(scaleKey);
        const lastAt = ScaleStream.lastReadingAt(which);
        if (lastAt && now - lastAt < 15000) continue;
        this._scaleLastHealAt[scaleKey] = now;
        console.warn(`[地磅 ${scaleKey}] 15s 无读数推送, 触发新鲜度自愈`);
        let server = null;
        try { server = await ScaleClient.getStatus(); } catch (e) { continue; /* 服务离线交给 WS 重连/自动连接兜底 */ }
        const one = server && server[which];
        const serverFresh = !!(one && one.connected && one.lastReadAt && (now - one.lastReadAt < 5000));
        if (serverFresh) {
          // 服务端有新鲜数据但页面收不到 → WS 推送通道僵死, 强制重建 WS
          ScaleStream.forceReconnect();
          this._reconcileScaleStatus();
        } else {
          // 服务端也拿不到数据 → 串口侧僵死, 强制关重开（force 绕过同参数幂等短路）
          const comPort = scaleComPortMap()[scaleKey];
          if (!comPort) continue;
          const baudRate = scaleBaudMap()[scaleKey] || 9600;
          const protocol = scaleProtocolMap()[scaleKey] || "yaohua-continuous";
          try {
            await ScaleClient.openScale({ scaleKey: which, port: comPort, baudRate: parseInt(baudRate, 10) || 9600, protocol, force: true });
            this._scaleConnectedAt[scaleKey] = Date.now();
          } catch (e) {
            console.warn(`[地磅 ${scaleKey}] 自愈重开失败: ${(e && e.message) || e}`);
          }
        }
      }
    },

    _unsubscribeScaleStream() {
      if (this._scaleUnsub) { this._scaleUnsub(); this._scaleUnsub = null; }
    },

    // [2026-08-14] 地磅运行时启动: jinglun 模式订阅 WS 实时推送 (web 模式不需要, 数据来自 WeighbridgeReader
    // 回调), 800ms 后尝试自动连接已授权的磅. 抽成方法: 本机无外设硬件切回启用时 (GlobalScaleBar watch) 复用.
    // [2026-08-15] 修复: 原写在 mixin 顶层自定义选项, Vue 2 不会把顶层选项挂到实例 this 上,
    //   mounted 调用 this._startScaleRuntime() 抛 TypeError → WS 不订阅 + 不自动连接 →
    //   jinglun 模式地磅显示不了数字. 必须放 methods 里 (与 _subscribeScaleStream 同理).
    _startScaleRuntime() {
      if (this._isHardwareDisabled()) return; // 本机无外设硬件: 不订阅 WS、不自动连接
      if (getScaleConnectMode() === "jinglun") {
        this._subscribeScaleStream();
        this._reconcileScaleStatus(); // 首次也要对账(WS 可能早已连接, onOpen 不会再触发)
        this._startScaleFreshnessWatchdog(); // [2026-08-24] 读数卡死自愈
      }
      this._autoConnectTimer = setTimeout(() => {
        this._autoConnectTimer = null;
        this.tryAutoConnectAuthorizedScales();
      }, 800);
    },

    // [2026-08-14] 地磅运行时停用: 取消自动连接 + 退订 WS + 断开全部连接.
    // [2026-08-17] jinglun 模式下串口归服务端持有: 默认只退订 WS, 不关服务端串口 —
    //   刷新/关标签页/组件卸载不再杀掉全局连接（多标签页共享服务端连接, 互不干扰）;
    //   仅"本机无外设硬件"切换等明确场景传 closeServicePort=true 才真正关闭（服务端 desired=false）。
    _stopScaleRuntime(closeServicePort) {
      if (this._autoConnectTimer) { clearTimeout(this._autoConnectTimer); this._autoConnectTimer = null; }
      if (this._autoConnectRetryTimer) { clearTimeout(this._autoConnectRetryTimer); this._autoConnectRetryTimer = null; }
      this._stopScaleFreshnessWatchdog();
      this._unsubscribeScaleStream();
      if (getScaleConnectMode() === "jinglun") {
        if (closeServicePort) {
          // [2026-09-16] 连接日志: 停用硬件/离开页面关服务端串口是明确动作, 不记"未知"
          ["entryScale", "exitScale"].forEach((k) =>
            noteDisconnectReason(k, "page_exit", "停用外设硬件/离开页面导致断开"));
          this.disconnectAllScales();
        }
        return;
      }
      // [2026-09-16] 连接日志: web 模式生命周期收口(切页/刷新/停用硬件)会关串口,
      // 属正常动作而非不稳定, 断开归因 page_exit（10s 内生效, 不污染之后的真实断线）
      ["entryScale", "exitScale"].forEach((k) =>
        noteDisconnectReason(k, "page_exit", "页面切换/刷新/关闭导致串口断开（web 模式正常动作）"));
      this.disconnectAllScales();
    },

    // [2026-08-15] 同上从顶层移入 methods: 原顶层定义时 this._onBeforeUnloadScale 为 undefined,
    //   addEventListener 收到 undefined 虽不报错但刷新页面时不会断开串口.
    // [2026-08-17] jinglun 模式: 页面卸载不关服务端串口（服务端调和循环自持, beforeunload 里的
    //   异步 close 请求还经常被浏览器掐掉, 多标签页下反而制造"偶发断连"）; web 模式行为不变。
    _onBeforeUnloadScale() {
      if (getScaleConnectMode() === "jinglun") return;
      this.disconnectAllScales();
    },
  },

  mounted() {
    // [2026-09-15] 连接日志：进入系统记 session_start + 订阅全局连接状态（先于自动连接）
    initScaleConnectLog(this.$store);
    window.addEventListener("beforeunload", this._onBeforeUnloadScale);
    this._startScaleRuntime();
  },

  beforeDestroy() {
    window.removeEventListener("beforeunload", this._onBeforeUnloadScale);
    // 清读数节流的 trailing 定时器 (防组件销毁后仍 commit)
    if (this._scaleThrottle) {
      Object.keys(this._scaleThrottle).forEach((k) => {
        const rec = this._scaleThrottle[k];
        if (rec && rec.timer) clearTimeout(rec.timer);
      });
      this._scaleThrottle = null;
    }
    this._stopScaleRuntime();
  },
};
